import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { GridContainerContext } from '@parchment/atoms';
import { VALIDATION_STATES } from '../../utils/constants';
import FieldLabel from '../FieldLabel/FieldLabel';
import FieldValidation from '../FieldValidation/FieldValidation';
import ValidatedFieldTooltip from './ValidatedFieldTooltip';
import nativeValidator from '../FieldValidation/nativeValidator';
const noop = () => undefined;
let counter = 0;

/**
 * Composes FieldValidation, FieldLabel and Tooltip around some given (via function as a child) field.
 * The function as a child is responsible for rendering the the field itself and invoking
 * the doValidation method. Validation status is subsequently passed back to the function
 * as a child so it can re-render with the updated validation state if it so chooses.
 */
export default function ComposedField(_ref) {
  let {
    required = false,
    maxCharacterLimit,
    onValidate = noop,
    validators = [],
    className,
    style,
    children,
    visited: visitedProp = false,
    autofillSection,
    autofillHints,
    loading = false,
    tooltipPlacement = 'above',
    ...props
  } = _ref;
  const containerRef = useRef(null);
  const [instanceId] = useState(() => counter++);
  const [labelText, setLabelText] = useState('');
  const [visited, setVisited] = useState(visitedProp);
  useEffect(() => setVisited(visitedProp), [visitedProp]);
  const id = props.id || `ComposedField-${instanceId}`;
  const label = props.label || props.placeholder || props.name;
  const autoComplete = !props.autoComplete ? props.autoComplete : `${autofillSection ? `section-${autofillSection}` : ''} ${Array.isArray(autofillHints) ? autofillHints.join(' ') : autofillHints || ''} ${props.autoComplete}`.trim().replace(/ +/, ' ');
  return /*#__PURE__*/React.createElement(GridContainerContext.Consumer, null, _ref2 => {
    let {
      edges
    } = _ref2;
    const internalLabel = typeof props.internalLabel === 'boolean' ? props.internalLabel : edges.length > 0;
    const internalLabelProps = internalLabel ? {
      placeholder: props.placeholder || labelText
    } : {};
    return /*#__PURE__*/React.createElement(Container, {
      ref: containerRef,
      className: className,
      style: style
    }, /*#__PURE__*/React.createElement(FieldValidation, {
      required: required,
      maxCharacterLimit: maxCharacterLimit,
      validators: [...validators, nativeValidator(containerRef)],
      onValidate: onValidate
    }, (valid, doValidation, _ref3) => {
      let {
        errorMessage
      } = _ref3;
      return /*#__PURE__*/React.createElement(DoValidation, {
        doValidation: doValidation,
        value: props.value,
        required: required,
        validators: validators,
        maxCharacterLimit: maxCharacterLimit
      }, /*#__PURE__*/React.createElement(FieldLabel, {
        id: id,
        label: label,
        required: required,
        internalLabel: internalLabel,
        onLabelText: setLabelText,
        scale: props.scale,
        loading: loading
      }, /*#__PURE__*/React.createElement(ValidatedFieldTooltip, {
        id: "validatedFieldTooltip",
        valid: visited ? valid : VALIDATION_STATES.neutral,
        fieldDisabled: props.disabled,
        internalLabel: internalLabel,
        label: label || 'Enter a value',
        error: errorMessage,
        placement: tooltipPlacement
      },
      // eslint-disable-next-line
      // @ts-ignore ... idk how to get that working here ... but whe implemented (i.e. ComposedTextField) it seems to work fine
      children({
        ...internalLabelProps,
        ...props,
        'aria-label': label,
        id,
        required,
        maxCharacterLimit,
        internalLabel,
        valid: visited ? valid : VALIDATION_STATES.neutral,
        doValidation,
        visited,
        autoComplete,
        loading,
        onBlur: e => {
          setVisited(true);
          props.onBlur && props.onBlur(e);
        }
      }))));
    }));
  });
}
function DoValidation(_ref4) {
  let {
    children,
    doValidation,
    value,
    required,
    maxCharacterLimit
  } = _ref4;
  useEffect(() => {
    doValidation(value).catch(noop);
  }, [value, required, maxCharacterLimit]);
  return children;
}
const Container = /*#__PURE__*/styled.div.withConfig({
  displayName: "ComposedField__Container",
  componentId: "sc-10shi6n-0"
})(["&.gc-top,&.gc-right,&.gc-bottom,&.gc-left,&.gc-none{border-width:0;}"]);